/**
 * Cosmetic catalog. The server re-validates every selection. An item is one of:
 * free (starter), premium (subscription), earned by mission days, bought in the
 * shop for coins (`price`), a battle-pass reward (`pass`) or an end-of-season
 * tier reward (`season`) or the 7th day of the login streak (`loginStreak`).
 * Owned items (shop, pass, season, login) live in user_items.
 */
interface Unlock {
  premium: boolean;
  /** Earned, not bought: needs this many completed mission days (see missions.ts). */
  missionDays?: number;
  /** Shop price in coins. */
  price?: number;
  /** Only from the battle pass. */
  pass?: boolean;
  /** Only as an end-of-season reward for reaching this tier. */
  season?: string;
  /** Only from day 7 of the daily login streak (loginStreak.ts). */
  loginStreak?: boolean;
}

/** `lights`, `dragon` and `storm` belong to the signature skins (see SkinSignature). */
export type SkinPattern = 'solid' | 'stripes' | 'dots' | 'neon' | 'scales' | 'rainbow' | 'galaxy' | 'flame' | 'viper' | 'lights' | 'dragon' | 'storm';
/** Head decoration, drawn on top of the head (purely cosmetic). `diya` and `dragon` belong to signature skins. */
export type SkinAccessory = 'crown' | 'horns' | 'halo' | 'shades' | 'diya' | 'dragon';

/** How rare and fancy a skin is, lowest first. Legendary and Mythic skins have a signature. */
export const RARITIES = ['common', 'rare', 'epic', 'legendary', 'mythic'] as const;
export type Rarity = (typeof RARITIES)[number];
export const RARITY_COLOR: Record<Rarity, string> = { common: '#a3acc2', rare: '#3ea2ff', epic: '#b86bff', legendary: '#ffb020', mythic: '#ff4d6a' };

/**
 * A Legendary or Mythic skin's own effects: a trail while boosting, an effect on the snakes it eats,
 * and a body that comes alive (its pattern). They only play while boosting or on a kill, from the
 * renderer's fixed particle pool, so they cost nothing the rest of the time.
 */
export type SkinSignature = 'diwali' | 'dragon' | 'thunder' | 'royal';

export interface SkinDef extends Unlock {
  id: string;
  /** Colors for head and gradient along the body. */
  head: string;
  body: [string, string];
  glow: string;
  pattern: SkinPattern;
  /** Rainbow pattern: colors cycled along the body. */
  colors?: string[];
  accessory?: SkinAccessory;
  /** `viper`: a broad, flat, triangular head with slit-pupilled eyes and dark markings (default: round). */
  headShape?: 'viper';
  /** Not a player's choice: only the game uses it (the arena boss). */
  hidden?: boolean;
  /** Default common (custom snakes too). */
  rarity?: Rarity;
  signature?: SkinSignature;
  /** Sold only until this moment (ISO, UTC); whoever bought it keeps it. */
  limitedUntil?: string;
}

export const SKINS: SkinDef[] = [
  { id: 'emerald', head: '#34f5a0', body: ['#22c58b', '#0e7a5a'], glow: '#34f5a0', pattern: 'solid', premium: false, rarity: 'common' },
  { id: 'sunset', head: '#ffb347', body: ['#ff7e5f', '#c0392b'], glow: '#ff7e5f', pattern: 'solid', premium: false, rarity: 'common' },
  { id: 'ocean', head: '#5ee7ff', body: ['#2f9bff', '#1b4fd8'], glow: '#5ee7ff', pattern: 'solid', premium: false, rarity: 'common' },
  { id: 'grape', head: '#d58bff', body: ['#a855f7', '#6d28d9'], glow: '#c084fc', pattern: 'solid', premium: false, rarity: 'common' },
  { id: 'lemon', head: '#fff275', body: ['#facc15', '#ca8a04'], glow: '#fde047', pattern: 'stripes', premium: false, rarity: 'common' },
  { id: 'rose', head: '#ff8fb1', body: ['#f43f5e', '#9f1239'], glow: '#fb7185', pattern: 'dots', premium: false, rarity: 'common' },
  // More free starters, so a new player has patterned snakes to choose from on day one.
  { id: 'jungle', head: '#d9f99d', body: ['#65a30d', '#14532d'], glow: '#a3e635', pattern: 'scales', premium: false, rarity: 'common' },
  { id: 'bumblebee', head: '#fff3a3', body: ['#fbbf24', '#1c1917'], glow: '#fcd34d', pattern: 'stripes', premium: false, rarity: 'common' },
  { id: 'bubblegum', head: '#ffe4f3', body: ['#f472b6', '#a21caf'], glow: '#f9a8d4', pattern: 'dots', premium: false, rarity: 'common' },
  { id: 'glacier', head: '#e0f7ff', body: ['#38bdf8', '#1e3a8a'], glow: '#7dd3fc', pattern: 'scales', premium: false, rarity: 'common' },
  { id: 'neon-cyber', head: '#00fff0', body: ['#ff00e5', '#00fff0'], glow: '#ff00e5', pattern: 'neon', premium: true, accessory: 'shades', rarity: 'epic' },
  { id: 'molten', head: '#fff1a8', body: ['#ff6a00', '#ee0979'], glow: '#ff6a00', pattern: 'flame', premium: true, accessory: 'horns', rarity: 'epic' },
  { id: 'aurora', head: '#b9fbc0', body: ['#06d6a0', '#7b2ff7'], glow: '#06d6a0', pattern: 'rainbow', colors: ['#06d6a0', '#1b9aaa', '#7b2ff7', '#ef476f', '#ffd166'], premium: true, rarity: 'epic' },
  { id: 'gold-royal', head: '#fff8d6', body: ['#ffd700', '#b8860b'], glow: '#ffd700', pattern: 'scales', premium: true, accessory: 'crown', rarity: 'epic' },
  { id: 'void', head: '#e0e0ff', body: ['#3a3a5c', '#0b0b1a'], glow: '#8b8bff', pattern: 'galaxy', premium: true, accessory: 'halo', rarity: 'epic' },
  { id: 'jade-quest', head: '#c6ffdd', body: ['#00b09b', '#96c93d'], glow: '#7dffb3', pattern: 'scales', premium: false, missionDays: 3, rarity: 'rare' },
  { id: 'phoenix', head: '#fff3b0', body: ['#ff4e00', '#ec9f05'], glow: '#ff8c00', pattern: 'flame', premium: false, missionDays: 7, rarity: 'epic' },
  // Daily login streak, day 7
  { id: 'blaze', head: '#fff4d6', body: ['#ff3d77', '#ffb000'], glow: '#ff7a3d', pattern: 'rainbow', colors: ['#ff3d77', '#ff7a3d', '#ffb000', '#ff7a3d'], accessory: 'crown', premium: false, loginStreak: true, rarity: 'epic' },
  // Shop (coins)
  { id: 'toxic', head: '#e6ff7a', body: ['#a3e635', '#3f6212'], glow: '#bef264', pattern: 'dots', premium: false, price: 300, rarity: 'rare' },
  { id: 'candy', head: '#ffd6ec', body: ['#ff7ac6', '#7ad7ff'], glow: '#ffb3e0', pattern: 'rainbow', colors: ['#ff7ac6', '#ffffff', '#7ad7ff'], premium: false, price: 400, rarity: 'rare' },
  { id: 'sapphire', head: '#d6e4ff', body: ['#3b82f6', '#1e1b8a'], glow: '#93c5fd', pattern: 'scales', premium: false, price: 500, rarity: 'rare' },
  { id: 'ember', head: '#ffe0b3', body: ['#f97316', '#7c2d12'], glow: '#fb923c', pattern: 'flame', premium: false, price: 650, rarity: 'rare' },
  // Vipers (pit-viper, russell-viper) have triangular heads; Russell's viper wears its chain of dark blotches ringed black and pale.
  { id: 'pit-viper', head: '#c9f26b', body: ['#74c93a', '#2c6e19'], glow: '#b5ef4a', pattern: 'scales', headShape: 'viper', premium: false, price: 700, rarity: 'epic' },
  { id: 'frost', head: '#f0fdff', body: ['#a5f3fc', '#0e7490'], glow: '#cffafe', pattern: 'scales', accessory: 'halo', premium: false, price: 800, rarity: 'epic' },
  { id: 'russell-viper', head: '#d8b083', body: ['#c0925e', '#6e4a28'], glow: '#e2b47c', pattern: 'viper', headShape: 'viper', premium: false, price: 900, rarity: 'epic' },
  // Legendary (coins): each with its own boost trail, kill effect and living pattern. Diwali is sold for the festival only.
  { id: 'diwali', head: '#ffd98a', body: ['#9c1a5c', '#2b0a40'], glow: '#ffb627', pattern: 'lights', accessory: 'diya', premium: false, price: 1000, rarity: 'legendary', signature: 'diwali', limitedUntil: '2026-11-16T00:00:00Z' },
  { id: 'dragon', head: '#ff7b47', body: ['#e0301e', '#4d0808'], glow: '#ff9f1c', pattern: 'dragon', accessory: 'dragon', premium: false, price: 1800, rarity: 'legendary', signature: 'dragon' },
  { id: 'thunder', head: '#d4f6ff', body: ['#3a86ff', '#26064f'], glow: '#7fe7ff', pattern: 'storm', premium: false, price: 1800, rarity: 'legendary', signature: 'thunder' },
  // Battle pass
  { id: 'tiger', head: '#fff1c1', body: ['#f59e0b', '#1f1300'], glow: '#fbbf24', pattern: 'stripes', premium: false, pass: true, rarity: 'rare' },
  { id: 'galaxy', head: '#e9d5ff', body: ['#6d28d9', '#0f172a'], glow: '#a78bfa', pattern: 'galaxy', premium: false, pass: true, rarity: 'epic' },
  { id: 'hydra', head: '#ccfbf1', body: ['#14b8a6', '#4c1d95'], glow: '#5eead4', pattern: 'scales', accessory: 'horns', premium: false, pass: true, rarity: 'epic' },
  { id: 'season-viper', head: '#fef08a', body: ['#22c55e', '#052e16'], glow: '#facc15', pattern: 'neon', accessory: 'shades', premium: false, pass: true, rarity: 'epic' },
  // End-of-season tier rewards
  { id: 'crown-gold', head: '#fff7cc', body: ['#ff9f43', '#b45309'], glow: '#ffd166', pattern: 'scales', accessory: 'crown', premium: false, season: 'crown', rarity: 'epic' },
  { id: 'ace-blaze', head: '#ffe4e6', body: ['#ff4d6d', '#7f1d1d'], glow: '#ff8fa3', pattern: 'flame', accessory: 'horns', premium: false, season: 'ace', rarity: 'epic' },
  { id: 'conqueror', head: '#f3e8ff', body: ['#b388ff', '#1e1036'], glow: '#d8b4fe', pattern: 'galaxy', accessory: 'crown', premium: false, season: 'conqueror', rarity: 'mythic', signature: 'royal' },
  // The arena boss's own look (never offered to players).
  { id: 'boss', head: '#ffe8a3', body: ['#c026d3', '#2e1065'], glow: '#ffd166', pattern: 'scales', accessory: 'crown', premium: false, hidden: true },
];

/** Skins a player can see and pick (everything but the game's own, like the boss). */
export const PLAYER_SKINS = SKINS.filter((s) => !s.hidden);
export const BOSS_SKIN = 'boss';

export const skinRarity = (s: SkinDef): Rarity => s.rarity ?? 'common';

/** Whether a limited item can still be bought (anything without an end date always can). */
export const onSale = (u: Pick<SkinDef, 'premium' | 'limitedUntil'>, now = Date.now()) => !u.limitedUntil || now < Date.parse(u.limitedUntil);

/**
 * What bots wear, by seat: mostly Legendary, Epic and premium skins (a few starters in between), so
 * players see the fancy ones in action and want them. A limited skin drops out once its sale ends.
 * The first 12 (an arena's bots) favour skins that are cheap to draw: neon, flame, scales, not galaxy.
 */
const BOT_SKIN_CYCLE = ['dragon', 'neon-cyber', 'thunder', 'sunset', 'diwali', 'molten', 'gold-royal', 'ocean', 'phoenix', 'aurora', 'pit-viper', 'blaze', 'frost', 'grape', 'void', 'galaxy'];
export function botSkins(now = Date.now()): string[] {
  return BOT_SKIN_CYCLE.filter((id) => onSale(findSkin(id), now));
}

/**
 * Bots in a small match (a room or practice, up to 8 snakes and the whole board on screen at once): only
 * two or three wear a Legendary skin, the rest starters, so the board isn't a wall of effects.
 */
const SMALL_BOT_SKIN_CYCLE = ['dragon', 'sunset', 'ocean', 'thunder', 'grape', 'lemon', 'diwali', 'rose', 'emerald', 'jungle'];
export function smallMatchBotSkins(now = Date.now()): string[] {
  return SMALL_BOT_SKIN_CYCLE.filter((id) => onSale(findSkin(id), now));
}

/**
 * What a snake's crash looks like to everyone in the match. Drawn once, when the snake dies, so they
 * cost nothing while playing. `classic` is everyone's to start with.
 */
export interface DeathEffectDef extends Unlock {
  id: string;
  /** Shown in the shop and in My snake. */
  icon: string;
}

export const DEATH_EFFECTS: DeathEffectDef[] = [
  { id: 'classic', icon: '💥', premium: false },
  { id: 'confetti', icon: '🎉', premium: false, price: 350 },
  { id: 'shockwave', icon: '🌀', premium: false, price: 500 },
  { id: 'lightning', icon: '⚡', premium: false, price: 650 },
  { id: 'fireworks', icon: '🎆', premium: false, price: 900 },
];
export const DEFAULT_DEATH_EFFECT = 'classic';
export const findDeathEffect = (id: string | undefined): DeathEffectDef => DEATH_EFFECTS.find((e) => e.id === id) ?? DEATH_EFFECTS[0]!;

/** Background texture of an arena theme. */
export type ThemePattern = 'grid' | 'weave' | 'tiles' | 'dots' | 'hex' | 'stars' | 'waves' | 'neon' | 'checker' | 'plain';

export interface ThemeDef extends Unlock {
  id: string;
  background: string;
  /** Second background color: the edges of the arena fade toward it. */
  background2?: string;
  pattern?: ThemePattern;
  gridLine: string;
  border: string;
  food: string;
  golden: string;
}

export const THEMES: ThemeDef[] = [
  { id: 'midnight', background: '#1a2550', background2: '#111a3a', pattern: 'checker', gridLine: 'rgba(120,140,255,0.06)', border: '#2f9bff', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'soft-navy', background: '#121a3a', background2: '#0c1229', pattern: 'checker', gridLine: 'rgba(120,140,255,0.05)', border: '#2f9bff', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'smooth-navy', background: '#172147', background2: '#0a0f24', pattern: 'plain', gridLine: 'rgba(120,140,255,0.05)', border: '#2f9bff', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'indigo', background: '#2a3268', background2: '#1b2250', pattern: 'checker', gridLine: 'rgba(160,150,255,0.06)', border: '#a855f7', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'teal', background: '#123640', background2: '#0a2229', pattern: 'checker', gridLine: 'rgba(120,255,220,0.06)', border: '#34f5a0', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'moss', background: '#1d3a1f', background2: '#112514', pattern: 'checker', gridLine: 'rgba(170,255,140,0.06)', border: '#7ddc5a', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'plum', background: '#35193f', background2: '#21102a', pattern: 'checker', gridLine: 'rgba(240,160,255,0.06)', border: '#d58bff', food: '#ff6b81', golden: '#ffd166', premium: false },
  { id: 'graphite', background: '#262a33', background2: '#17191f', pattern: 'checker', gridLine: 'rgba(255,255,255,0.05)', border: '#a3acd1', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'mint-light', background: '#e4f7f0', background2: '#cfeee4', pattern: 'checker', gridLine: 'rgba(0,0,0,0.05)', border: '#2f9bff', food: '#e63946', golden: '#f4a261', premium: false },
  { id: 'sky-light', background: '#e3f1ff', background2: '#cde3fa', pattern: 'checker', gridLine: 'rgba(0,0,0,0.05)', border: '#2f9bff', food: '#e63946', golden: '#f4a261', premium: false },
  { id: 'weave', background: '#0d1430', background2: '#070a18', pattern: 'weave', gridLine: 'rgba(120,140,255,0.06)', border: '#3b4bff', food: '#ff4d6d', golden: '#ffd166', premium: false },
  { id: 'forest', background: '#12291c', background2: '#08150e', pattern: 'tiles', gridLine: 'rgba(120,255,180,0.06)', border: '#2bd67b', food: '#ff8a5b', golden: '#ffe66d', premium: false },
  { id: 'paper', background: '#f8f5ec', background2: '#e9e2d0', pattern: 'grid', gridLine: 'rgba(0,0,0,0.06)', border: '#22223b', food: '#e63946', golden: '#f4a261', premium: false },
  { id: 'synthwave', background: '#24103d', background2: '#0c0418', pattern: 'neon', gridLine: 'rgba(255,0,200,0.10)', border: '#ff2fd6', food: '#00f5ff', golden: '#ffe600', premium: true },
  { id: 'deep-sea', background: '#04243d', background2: '#010b16', pattern: 'waves', gridLine: 'rgba(0,200,255,0.08)', border: '#03a9f4', food: '#ff6f61', golden: '#ffd54f', premium: true },
  { id: 'lava', background: '#2a0f07', background2: '#100402', pattern: 'hex', gridLine: 'rgba(255,120,0,0.08)', border: '#ff5722', food: '#ffeb3b', golden: '#ffffff', premium: true },
  { id: 'royal-night', background: '#1a0f33', background2: '#0a0516', pattern: 'stars', gridLine: 'rgba(200,160,255,0.08)', border: '#b388ff', food: '#ff80ab', golden: '#ffe082', premium: false, missionDays: 14 },
  { id: 'sakura', background: '#2a1420', background2: '#12070d', pattern: 'dots', gridLine: 'rgba(255,180,210,0.08)', border: '#ff9ec7', food: '#ffd1e6', golden: '#fff1a8', premium: false, price: 500 },
  { id: 'arctic', background: '#0e2433', background2: '#050f17', pattern: 'hex', gridLine: 'rgba(180,240,255,0.08)', border: '#9be7ff', food: '#ff7a8a', golden: '#fff5c2', premium: false, price: 500 },
  { id: 'neon-grid', background: '#0a0420', background2: '#020008', pattern: 'neon', gridLine: 'rgba(0,255,200,0.12)', border: '#00ffc8', food: '#ff2bd6', golden: '#fff200', premium: false, pass: true },
  { id: 'sunrise', background: '#2b160c', background2: '#120804', pattern: 'waves', gridLine: 'rgba(255,190,120,0.08)', border: '#ffb347', food: '#ff5e62', golden: '#fff3b0', premium: false, pass: true },
];

export const DEFAULT_SKIN = 'emerald';
export const DEFAULT_THEME = 'midnight';

/**
 * Custom snakes: a player-made skin, stored and sent as an id like "c.3.b.stripes.crown"
 * (body color index, tail color index, pattern, accessory or "none"), so it fits everywhere a skin id goes.
 * Colors and the basic patterns are free; a fancier pattern or an accessory needs a skin that has it.
 */
export const CUSTOM_COLORS: Array<{ id: string; light: string; base: string; dark: string }> = [
  { id: 'mint', light: '#b6ffe0', base: '#34f5a0', dark: '#0e7a5a' },
  { id: 'lime', light: '#ecffb0', base: '#a3e635', dark: '#3f6212' },
  { id: 'lemon', light: '#fff8b5', base: '#facc15', dark: '#a16207' },
  { id: 'orange', light: '#ffd8a8', base: '#fb923c', dark: '#9a3412' },
  { id: 'coral', light: '#ffc2b3', base: '#ff6b5b', dark: '#9f1d1d' },
  { id: 'red', light: '#ffb3c0', base: '#ef4444', dark: '#7f1d1d' },
  { id: 'pink', light: '#ffd1ea', base: '#f472b6', dark: '#9d174d' },
  { id: 'magenta', light: '#ffc2ff', base: '#e040fb', dark: '#6a1b9a' },
  { id: 'violet', light: '#e2ccff', base: '#a855f7', dark: '#4c1d95' },
  { id: 'indigo', light: '#cfd4ff', base: '#6366f1', dark: '#1e1b6e' },
  { id: 'blue', light: '#c4e1ff', base: '#3b82f6', dark: '#1e3a8a' },
  { id: 'sky', light: '#d0f4ff', base: '#38bdf8', dark: '#075985' },
  { id: 'teal', light: '#c4fff4', base: '#14b8a6', dark: '#134e4a' },
  { id: 'brown', light: '#f0d9c0', base: '#a16207', dark: '#422006' },
  { id: 'white', light: '#ffffff', base: '#e2e8f0', dark: '#64748b' },
  { id: 'black', light: '#94a3b8', base: '#334155', dark: '#0b1020' },
];
/** Patterns a custom snake can use; the first four are free. (Rainbow needs its own palette, so it stays on ready-made skins.) */
export const CUSTOM_PATTERNS: SkinPattern[] = ['solid', 'stripes', 'dots', 'scales', 'neon', 'flame', 'galaxy'];
const FREE_CUSTOM_PATTERNS = new Set<SkinPattern>(['solid', 'stripes', 'dots', 'scales']);
export const CUSTOM_ACCESSORIES: SkinAccessory[] = ['crown', 'horns', 'halo', 'shades'];

export interface CustomSkin {
  body: number;
  tail: number;
  pattern: SkinPattern;
  accessory: SkinAccessory | null;
}

export const isCustomSkinId = (id: string) => id.startsWith('c.');

export function customSkinId(c: CustomSkin): string {
  return `c.${c.body.toString(16)}.${c.tail.toString(16)}.${c.pattern}.${c.accessory ?? 'none'}`;
}

export function parseCustomSkin(id: string): CustomSkin | null {
  const m = /^c\.([0-9a-f])\.([0-9a-f])\.([a-z]+)\.([a-z]+)$/.exec(id);
  if (!m) return null;
  const body = parseInt(m[1]!, 16);
  const tail = parseInt(m[2]!, 16);
  const pattern = m[3] as SkinPattern;
  const accessory = m[4] === 'none' ? null : (m[4] as SkinAccessory);
  if (body >= CUSTOM_COLORS.length || tail >= CUSTOM_COLORS.length) return null;
  if (!CUSTOM_PATTERNS.includes(pattern)) return null;
  if (accessory !== null && !CUSTOM_ACCESSORIES.includes(accessory)) return null;
  return { body, tail, pattern, accessory };
}

function customSkinDef(id: string, c: CustomSkin): SkinDef {
  const a = CUSTOM_COLORS[c.body]!;
  const b = CUSTOM_COLORS[c.tail]!;
  return {
    id,
    head: a.light,
    body: [a.base, b.dark],
    glow: a.base,
    pattern: c.pattern,
    ...(c.accessory ? { accessory: c.accessory } : {}),
    premium: false,
  };
}

export const findSkin = (id: string): SkinDef => {
  const custom = isCustomSkinId(id) ? parseCustomSkin(id) : null;
  if (custom) return customSkinDef(id, custom);
  return SKINS.find((s) => s.id === id) ?? SKINS[0]!;
};

/** A background a player mixes themselves: any color under one of the plain textures. The textures of themes that are sold or earned stay out. */
export const CUSTOM_THEME_PATTERNS = ['checker', 'plain', 'grid', 'dots', 'tiles', 'weave'] as const satisfies readonly ThemePattern[];
export type CustomThemePattern = (typeof CUSTOM_THEME_PATTERNS)[number];

export interface CustomTheme {
  pattern: CustomThemePattern;
  /** Lowercase #rrggbb. */
  color: string;
}

export const DEFAULT_CUSTOM_THEME: CustomTheme = { pattern: 'checker', color: '#1a2550' };

export const isCustomThemeId = (id: string) => id.startsWith('t.');

export function customThemeId(c: CustomTheme): string {
  return `t.${c.pattern}.${c.color.slice(1)}`;
}

export function parseCustomTheme(id: string): CustomTheme | null {
  const m = /^t\.([a-z]+)\.([0-9a-f]{6})$/.exec(id);
  if (!m) return null;
  const pattern = m[1] as CustomThemePattern;
  if (!CUSTOM_THEME_PATTERNS.includes(pattern)) return null;
  return { pattern, color: `#${m[2]}` };
}

/** `#rrggbb` moved toward `to` (0..255 grey) by `k` (0..1). */
function shadeHex(hex: string, to: number, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const part = (v: number) => Math.round(v + (to - v) * k).toString(16).padStart(2, '0');
  return `#${part((n >> 16) & 255)}${part((n >> 8) & 255)}${part(n & 255)}`;
}

function customThemeDef(id: string, c: CustomTheme): ThemeDef {
  const n = parseInt(c.color.slice(1), 16);
  const light = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 150;
  return {
    id,
    background: c.color,
    background2: shadeHex(c.color, 0, light ? 0.1 : 0.32),
    pattern: c.pattern,
    gridLine: light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
    border: light ? shadeHex(c.color, 0, 0.55) : shadeHex(c.color, 255, 0.45),
    food: light ? '#e63946' : '#ff4d6d',
    golden: light ? '#f4a261' : '#ffd166',
    premium: false,
  };
}

export const findTheme = (id: string): ThemeDef => {
  const custom = isCustomThemeId(id) ? parseCustomTheme(id) : null;
  if (custom) return customThemeDef(id, custom);
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
};

/** Free for everyone: not premium, earned, sold or rewarded (and not one of the game's own skins). */
const isFree = (u: Unlock & { hidden?: boolean }) => !u.hidden && !u.premium && !u.missionDays && !u.price && !u.pass && !u.season && !u.loginStreak;

/** Free skins anyone may be assigned (e.g. bots, or to break a same-skin tie in a match). */
export const isStarterSkin = (s: SkinDef) => isFree(s);

/** Key of an owned item in user_items / the owned set. */
/** The kinds of cosmetic a player can own and pick. */
export type CosmeticKind = 'skin' | 'theme' | 'effect';
export const itemKey = (kind: CosmeticKind, id: string) => `${kind}:${id}`;

export function findCosmetic(kind: CosmeticKind, id: string): SkinDef | ThemeDef | DeathEffectDef | undefined {
  if (kind === 'effect') return DEATH_EFFECTS.find((e) => e.id === id);
  return kind === 'skin' ? SKINS.find((s) => s.id === id) : THEMES.find((t) => t.id === id);
}

/**
 * Whether a player may use an item. `owned` holds itemKey()s bought in the shop
 * or won from the pass or a season.
 */
export function canUseCosmetic(kind: CosmeticKind, id: string, isPremium: boolean, missionDays = 0, owned: ReadonlySet<string> = new Set()): boolean {
  if (kind === 'skin' && isCustomSkinId(id)) {
    const c = parseCustomSkin(id);
    return !!c && customPartUsable('pattern', c.pattern, isPremium, missionDays, owned) && (c.accessory === null || customPartUsable('accessory', c.accessory, isPremium, missionDays, owned));
  }
  if (kind === 'theme' && isCustomThemeId(id)) return !!parseCustomTheme(id);
  const item = findCosmetic(kind, id);
  if (!item) return false;
  if (isFree(item)) return true;
  if (owned.has(itemKey(kind, id))) return true;
  if (item.premium) return isPremium;
  if (item.missionDays) return missionDays >= item.missionDays;
  return false;
}

/**
 * Whether a custom snake may use this pattern or accessory: the basic patterns are free, anything else
 * needs one ready-made skin with it that the player can already use (so it's earned the same way).
 */
export function customPartUsable(
  part: 'pattern' | 'accessory',
  value: SkinPattern | SkinAccessory,
  isPremium: boolean,
  missionDays = 0,
  owned: ReadonlySet<string> = new Set(),
): boolean {
  if (part === 'pattern' && FREE_CUSTOM_PATTERNS.has(value as SkinPattern)) return true;
  return SKINS.some((s) => (part === 'pattern' ? s.pattern === value : s.accessory === value) && canUseCosmetic('skin', s.id, isPremium, missionDays, owned));
}

/** The ready-made skins that would unlock a custom pattern or accessory (for "get it from …" hints). */
export function customPartSources(part: 'pattern' | 'accessory', value: SkinPattern | SkinAccessory): SkinDef[] {
  return SKINS.filter((s) => (part === 'pattern' ? s.pattern === value : s.accessory === value));
}
